import type { LottieHandle } from './types';
import { invalidateCanvas } from '../render/canvasInvalidate';

export interface MountOpts {
  loop: boolean;
  autoplay: boolean;
  speed: number;
}

/**
 * Mount a (slot-resolved) Lottie document into `el` using lottie-web's SVG
 * renderer. SVG output is plain DOM, so it paints through drawElementImage in
 * unified mode — the canvas renderer we must NOT use (nested canvas inside
 * layoutsubtree is broken, WICG #120). The build is lazy-imported: zero cost
 * until the first lottie element. enterFrame → invalidateCanvas keeps the big
 * canvas repainting while the clip animates (same contract as scripts).
 */
export async function mountLottie(el: HTMLElement, animationData: unknown, opts: MountOpts): Promise<LottieHandle> {
  const lottie = (await import('lottie-web/build/player/lottie_svg')).default;
  const anim = lottie.loadAnimation({
    container: el,
    renderer: 'svg',
    loop: opts.loop,
    autoplay: opts.autoplay,
    animationData,
  });
  if (opts.speed !== 1) anim.setSpeed(opts.speed);
  anim.addEventListener('enterFrame', () => invalidateCanvas());

  const fr = (animationData as { fr?: number }).fr || 60;
  const durationMs = anim.getDuration(false) * 1000; // seconds → ms

  return {
    play: () => anim.play(),
    pause: () => anim.pause(),
    seek: (ms: number) => anim.goToAndStop((Math.max(0, Math.min(ms, durationMs)) / 1000) * fr, true),
    isPlaying: () => !anim.isPaused,
    durationMs,
    destroy: () => anim.destroy(),
  };
}
